import React, { useRef, useState } from "react";
import { OverflowList } from "@design-system/OverflowList/OverflowList";
import { Button } from "@design-system/Button/Button";
import { BooleanControl, SelectControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./structureLayoutControls.module.scss";

const filterLabels = [
    "Date",
    "Account",
    "Amount",
    "Category",
    "Entity",
    "Counterparty",
];

const itemCountOptions = ["2", "4", "6"] as const;

type ItemCountOption = (typeof itemCountOptions)[number];

const maxVisibleOptions = ["auto", "1", "2", "3"] as const;

type MaxVisibleOption = (typeof maxVisibleOptions)[number];

const containerWidthOptions = ["240", "300", "360"] as const;

type ContainerWidthOption = (typeof containerWidthOptions)[number];

export const OverflowListPlayground = () => {
    const [itemCount, setItemCount] = useState<ItemCountOption>("4");
    const [maxVisible, setMaxVisible] = useState<MaxVisibleOption>("2");
    const [containerWidth, setContainerWidth] =
        useState<ContainerWidthOption>("300");
    const [isOverflowButtonHidden, setIsOverflowButtonHidden] = useState(false);
    const [overflowItems, setOverflowItems] = useState<string[]>([]);

    const containerRef = useRef<HTMLDivElement>(null);
    const items = filterLabels.slice(0, Number(itemCount));

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Items</h2>
                        <div className={styles.fields}>
                            <SelectControl
                                label="Item count"
                                options={itemCountOptions}
                                value={itemCount}
                                onChange={setItemCount}
                            />
                            <SelectControl
                                label="Max visible"
                                options={maxVisibleOptions}
                                value={maxVisible}
                                onChange={setMaxVisible}
                            />
                            <SelectControl
                                label="Container width (px)"
                                options={containerWidthOptions}
                                value={containerWidth}
                                onChange={setContainerWidth}
                            />
                        </div>
                        <p className={styles.hint}>
                            With <code>maxVisible</code> set to auto the
                            component measures <code>containerRef</code>: one
                            item up to 500px, then one more per extra 200px, up
                            to five. Every width available in this narrow
                            preview therefore resolves to a single visible item.
                        </p>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Hide overflow button"
                                checked={isOverflowButtonHidden}
                                onChange={setIsOverflowButtonHidden}
                            />
                        </div>
                        <p className={styles.hint}>
                            In the app the overflow button opens the filters
                            overlay on the first hidden filter. Here it just
                            records the hidden items.
                        </p>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${items.length} items · max ${maxVisible}`}
                alignment="start"
                width="full"
                output={JSON.stringify(
                    {
                        componentProps: {
                            items,
                            maxVisible:
                                maxVisible === "auto"
                                    ? null
                                    : Number(maxVisible),
                            hideOverflowButton: isOverflowButtonHidden,
                            containerRef: `${containerWidth}px container`,
                        },
                        showcaseState: {
                            overflowItems,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div
                    ref={containerRef}
                    className={styles.overflowFrame}
                    style={{ width: `${containerWidth}px` }}
                >
                    <OverflowList
                        items={items}
                        containerRef={containerRef}
                        maxVisible={
                            maxVisible === "auto"
                                ? undefined
                                : Number(maxVisible)
                        }
                        hideOverflowButton={isOverflowButtonHidden}
                        renderItems={(visibleItems) =>
                            visibleItems.map((item) => (
                                <Button
                                    key={item}
                                    variant="secondary"
                                    size="sm"
                                >
                                    {item}
                                </Button>
                            ))
                        }
                        onOverflowClick={(hiddenItems) =>
                            setOverflowItems(hiddenItems)
                        }
                    />
                </div>
                <p className={styles.hint}>
                    The bordered frame is showcase CSS; its width is what{" "}
                    <code>containerRef</code> measures.
                    {overflowItems.length > 0
                        ? ` Last overflow click: ${overflowItems.join(", ")}.`
                        : ""}
                </p>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
